import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiBarChart2, FiChevronDown, FiSearch, FiX, FiFilter, FiChevronRight } from 'react-icons/fi';
import '../../styles/TransitionHeatmap.css';

// ── Types ──────────────────────────────────────────────────────────────────────
interface ToolCountDto { tool: string; count: number; }
interface PeriodDto {
  year: number; month?: number; period: number; periodLabel: string;
  totalCount: number; tools: ToolCountDto[];
}
interface SummaryDto {
  grandTotal: number; topTool: string; peakPeriodLabel: string;
  uniqueTotalTools: number; periodsReturned: number; avgPerPeriod: number;
}
interface ApiResponse { periods: PeriodDto[]; summary: SummaryDto; }

type GroupBy  = 'day' | 'week' | 'month';
type TopN     = 5 | 10 | 15 | 20 | 30 | 0;
type SortMode = 'default' | 'hi' | 'lo';

interface Filters {
  groupBy: GroupBy; topN: TopN;
  dateFrom: string; dateTo: string; toolSearch: string;
}

// ── Config ─────────────────────────────────────────────────────────────────────

const TOP_N_OPTIONS: { label: string; value: TopN }[] = [
  { label: 'Top 5',  value: 5  }, { label: 'Top 10', value: 10 },
  { label: 'Top 15', value: 15 }, { label: 'Top 20', value: 20 },
  { label: 'Top 30', value: 30 }, { label: 'All',    value: 0  },
];

// ── Category detection ─────────────────────────────────────────────────────────
interface Category { name: string; color: string; }
const CATEGORY_ORDER = ['Reads','Writes','Scans','Actions','Policy','Composio','Query','Other'];
const CATEGORY_COLORS: Record<string, string> = {
  Reads:    '#60a5fa',
  Writes:   '#34d399',
  Scans:    '#f97316',
  Actions:  '#a78bfa',
  Policy:   '#38bdf8',
  Composio: '#fb923c',
  Query:    '#e879f9',
  Other:    '#94a3b8',
};

function getCategory(tool: string): Category {
  const t = tool.toUpperCase();
  if (t.startsWith('GET-') || t.startsWith('GET_'))         return { name: 'Reads',    color: CATEGORY_COLORS.Reads    };
  if (t.startsWith('INSERT-') || t.startsWith('BULK-') ||
      t.startsWith('INSERT_') || t.startsWith('BULK_'))      return { name: 'Writes',   color: CATEGORY_COLORS.Writes   };
  if (t.endsWith('-SCAN')  || t.endsWith('_SCAN') ||
      t.includes('-SCAN-') || t.includes('DRIVING-LICENSE')) return { name: 'Scans',    color: CATEGORY_COLORS.Scans    };
  if (t.startsWith('SEND-') || t.startsWith('SEND_'))        return { name: 'Actions',  color: CATEGORY_COLORS.Actions  };
  if (t.startsWith('POLICY-') || t.startsWith('POLICY_'))    return { name: 'Policy',   color: CATEGORY_COLORS.Policy   };
  if (t.startsWith('COMPOSIO'))                              return { name: 'Composio', color: CATEGORY_COLORS.Composio };
  if (t.includes('EXECUTE') || t.includes('QUERY') ||
      t.includes('CSV-TO')  || t.includes('CSV_TO'))         return { name: 'Query',    color: CATEGORY_COLORS.Query    };
  return { name: 'Other', color: CATEGORY_COLORS.Other };
}

// ── Sparkline ──────────────────────────────────────────────────────────────────
const Sparkline: React.FC<{ values: number[]; color: string; w?: number; h?: number }> = ({
  values, color, w = 48, h = 14,
}) => {
  if (values.length < 2) return null;
  const max = Math.max(...values, 1);
  const pts = values.map((v, i) => [
    (i / (values.length - 1)) * w,
    h - Math.max((v / max) * h * 0.85, 0),
  ]);
  const linePath = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${w},${h} L0,${h} Z`;
  const hex  = color.replace('#', '');
  const r    = parseInt(hex.slice(0,2),16);
  const g    = parseInt(hex.slice(2,4),16);
  const b    = parseInt(hex.slice(4,6),16);
  return (
    <svg width={w} height={h} className="th-sparkline-svg">
      <path d={areaPath}  fill={`rgba(${r},${g},${b},0.12)`} />
      <path d={linePath}  fill="none" stroke={`rgba(${r},${g},${b},0.75)`}
        strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      {/* last point dot */}
      <circle cx={pts[pts.length-1][0].toFixed(1)} cy={pts[pts.length-1][1].toFixed(1)}
        r="1.8" fill={`rgba(${r},${g},${b},0.9)`} />
    </svg>
  );
};

// ── Delta badge ────────────────────────────────────────────────────────────────
const Delta: React.FC<{ pct: number | null }> = ({ pct }) => {
  if (pct === null) return null;
  const up    = pct >= 0;
  const label = `${up ? '↑' : '↓'} ${Math.abs(pct)}%`;
  return (
    <span className={`th-delta ${up ? 'th-delta--up' : 'th-delta--down'}`}>{label}</span>
  );
};

// ── Helpers ────────────────────────────────────────────────────────────────────
function shortTool(name: string, max = 16): string {
  return name.length > max ? name.slice(0, max - 1) + '…' : name;
}
function localDateISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function todayISO(): string { return localDateISO(new Date()); }
function daysAgoISO(n: number): string {
  const d = new Date(); d.setDate(d.getDate() - n);
  return localDateISO(d);
}

// ── Color scales ──────────────────────────────────────────────────────────────
const LEVEL_BG_RAW  = ['rgba(255,255,255,0.06)','rgba(72,30,160,0.65)','rgba(95,42,200,0.78)','rgba(115,52,228,0.88)','rgba(128,62,242,0.94)','rgba(139,92,246,1.00)'];
const LEVEL_BG_NORM = ['rgba(255,255,255,0.06)','rgba(28,70,185,0.65)','rgba(38,90,220,0.78)','rgba(52,110,242,0.88)','rgba(80,148,255,0.94)','rgba(110,180,255,1.00)'];
const LEVEL_SHADOW  = ['none','none','none','none','0 0 8px rgba(124,58,237,0.45)','0 0 14px rgba(139,92,246,0.65),0 0 30px rgba(139,92,246,0.22)'];

const getLevelBg = (norm: boolean, lv: number) => (norm ? LEVEL_BG_NORM : LEVEL_BG_RAW)[lv] ?? 'rgba(255,255,255,0.04)';

function getLevel(count: number, max: number): number {
  if (!count || !max) return 0;
  const t = count / max;
  if (t < 0.18) return 1; if (t < 0.38) return 2; if (t < 0.62) return 3; if (t < 0.84) return 4; return 5;
}
function getNormLevel(count: number, total: number): number {
  if (!count || !total) return 0;
  const p = count / total;
  if (p < 0.07) return 1; if (p < 0.15) return 2; if (p < 0.25) return 3; if (p < 0.38) return 4; return 5;
}

const SORT_CYCLE: SortMode[]               = ['default','hi','lo'];
const SORT_LABEL: Record<SortMode, string> = { default:'Default', hi:'Most ↓', lo:'Least ↑' };

// ── Component ─────────────────────────────────────────────────────────────────
const TransitionHeatmap: React.FC = () => {
  const [open,           setOpen]           = useState(true);
  const [sortMode,       setSortMode]       = useState<SortMode>('default');
  const [normalized,     setNormalized]     = useState(false);
  const [hoverTool,      setHoverTool]      = useState<string | null>(null);
  const [hoverPeriod,    setHoverPeriod]    = useState<number | null>(null);
  const [selectedCell,   setSelectedCell]   = useState<{ tool: string; pi: number } | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [tooltip, setTooltip] = useState<{ tool: string; pi: number; count: number; x: number; y: number } | null>(null);

  const [filters, setFilters] = useState<Filters>({
    groupBy: 'day', topN: 10,
    dateFrom: daysAgoISO(14), dateTo: todayISO(), toolSearch: '',
  });
  const [searchDraft, setSearchDraft] = useState('');
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [data,    setData]    = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async (f: Filters) => {
    setLoading(true); setError(null);
    try {
      const token  = localStorage.getItem('authToken') ?? '';
      const params = new URLSearchParams({
        groupBy: f.groupBy, topN: String(f.topN),
        ...(f.dateFrom   ? { dateFrom:   f.dateFrom   } : {}),
        ...(f.dateTo     ? { dateTo:     f.dateTo     } : {}),
        ...(f.toolSearch ? { toolSearch: f.toolSearch } : {}),
      });
      const res  = await fetch(`${(process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/momentumamp/tools?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(filters); }, []); // eslint-disable-line

  const applyFilter = <K extends keyof Filters>(key: K, val: Filters[K]) => {
    const next = { ...filters, [key]: val };
    setFilters(next); fetchData(next);
  };
  const onSearchChange = (val: string) => {
    setSearchDraft(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      const next = { ...filters, toolSearch: val };
      setFilters(next); fetchData(next);
    }, 400);
  };
  const clearSearch = () => {
    setSearchDraft('');
    const next = { ...filters, toolSearch: '' };
    setFilters(next); fetchData(next);
  };
  const resetFilters = () => {
    const def: Filters = { groupBy:'day', topN:10, dateFrom:daysAgoISO(14), dateTo:todayISO(), toolSearch:'' };
    setFilters(def); setSearchDraft(''); fetchData(def);
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const periods        = data?.periods ?? [];
  const summary        = data?.summary;

  const toolSet = useMemo(() => {
    const s = new Set<string>();
    periods.forEach(p => p.tools.forEach(t => s.add(t.tool)));
    return Array.from(s);
  }, [periods]);

  const toolTotals = useMemo(() => {
    const m: Record<string, number> = {};
    periods.forEach(p => p.tools.forEach(t => { m[t.tool] = (m[t.tool] ?? 0) + t.count; }));
    return m;
  }, [periods]);

  const sortedTools = useMemo(() => {
    if (sortMode === 'hi') return [...toolSet].sort((a,b) => (toolTotals[b]??0) - (toolTotals[a]??0));
    if (sortMode === 'lo') return [...toolSet].sort((a,b) => (toolTotals[a]??0) - (toolTotals[b]??0));
    return toolSet;
  }, [toolSet, toolTotals, sortMode]);

  // ── Category groups ────────────────────────────────────────────────────────
  const groupedTools = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const tool of sortedTools) {
      const cat = getCategory(tool).name;
      if (!map[cat]) map[cat] = [];
      map[cat].push(tool);
    }
    return CATEGORY_ORDER.filter(c => map[c]).map(c => ({ category: c, tools: map[c] }));
  }, [sortedTools]);

  const toggleGroup = (cat: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  };

  // ── WoW deltas ────────────────────────────────────────────────────────────
  const wowDeltas = useMemo(() => {
    if (periods.length < 2) return { overall: null as number | null, tools: {} as Record<string, number | null> };
    const last = periods[periods.length - 1];
    const prev = periods[periods.length - 2];
    const overall = prev.totalCount > 0
      ? Math.round(((last.totalCount - prev.totalCount) / prev.totalCount) * 100) : null;
    const tools: Record<string, number | null> = {};
    for (const tool of toolSet) {
      const lc = last.tools.find(t => t.tool === tool)?.count ?? 0;
      const pc = prev.tools.find(t => t.tool === tool)?.count ?? 0;
      tools[tool] = pc > 0 ? Math.round(((lc - pc) / pc) * 100) : null;
    }
    return { overall, tools };
  }, [periods, toolSet]);

  const maxVal         = useMemo(() => Math.max(...periods.flatMap(p => p.tools.map(t => t.count)), 1), [periods]);
  const maxPeriodTotal = useMemo(() => Math.max(...periods.map(p => p.totalCount), 1), [periods]);

  const toolStats = useMemo(() => Object.fromEntries(toolSet.map(tool => {
    const vals = periods.map(p => p.tools.find(t => t.tool === tool)?.count ?? 0);
    if (!vals.length) return [tool, { mean: 0, threshold: Infinity }];
    const mean   = vals.reduce((s,v)=>s+v,0)/vals.length;
    const stdDev = Math.sqrt(vals.reduce((s,v)=>s+(v-mean)**2,0)/vals.length);
    return [tool, { mean: Math.round(mean*10)/10, threshold: mean+1.6*stdDev }];
  })), [toolSet, periods]);

  const toolTrends = useMemo(() => {
    const mid = Math.floor(periods.length/2);
    return Object.fromEntries(toolSet.map(tool => {
      const vals = periods.map(p => p.tools.find(t => t.tool === tool)?.count ?? 0);
      const h1   = vals.slice(0,mid).reduce((s,v)=>s+v,0);
      const h2   = vals.slice(mid).reduce((s,v)=>s+v,0);
      const r    = h2/(h1||1);
      return [tool, r>1.12?'up':r<0.88?'down':'flat'];
    }));
  }, [toolSet, periods]);

  const peakCell = useMemo(() => {
    let best = { tool:'', pi:-1, count:0 };
    periods.forEach((p,pi) => p.tools.forEach(t => { if (t.count>best.count) best={tool:t.tool,pi,count:t.count}; }));
    return best;
  }, [periods]);

  const isAnomaly = (tool: string, count: number) => {
    const s = toolStats[tool];
    return !!s && count>0 && count>=s.threshold;
  };

  // ── Interactions ──────────────────────────────────────────────────────────
  const onEnter = (e: React.MouseEvent, tool: string, pi: number) => {
    setHoverTool(tool); setHoverPeriod(pi);
    const count = periods[pi]?.tools.find(t => t.tool === tool)?.count ?? 0;
    setTooltip({ tool, pi, count, x: e.clientX, y: e.clientY });
  };
  const onMove  = (e: React.MouseEvent) => setTooltip(p => p ? {...p, x:e.clientX, y:e.clientY} : null);
  const onLeave = () => { setHoverTool(null); setHoverPeriod(null); setTooltip(null); };
  const onCellClick = (tool: string, pi: number) =>
    setSelectedCell(p => p?.tool===tool && p?.pi===pi ? null : {tool,pi});

  const drill = useMemo(() => {
    if (!selectedCell) return null;
    const { tool, pi } = selectedCell;
    const period = periods[pi]; if (!period) return null;
    const count   = period.tools.find(t => t.tool === tool)?.count ?? 0;
    const stats   = toolStats[tool];
    const avg     = stats?.mean ?? 0;
    const diffPct = avg>0 ? Math.round(((count-avg)/avg)*100) : 0;
    const pctOf   = period.totalCount>0 ? Math.round((count/period.totalCount)*100) : 0;
    const level   = getLevel(count, maxVal);
    return { tool, pi, count, avg, diffPct, pctOf, level, period, catColor: getCategory(tool).color };
  }, [selectedCell, periods, toolStats, maxVal]);

  const activeFilterCount = [
    filters.dateFrom !== daysAgoISO(14), filters.dateTo !== todayISO(),
    filters.topN !== 10, filters.groupBy !== 'day', !!filters.toolSearch,
  ].filter(Boolean).length;

  const headerLabel = periods.length
    ? `${periods[0].periodLabel} – ${periods[periods.length-1].periodLabel}` : '—';

  // "today" detection — match by periodLabel so it works across groupBy modes
  const todayLabel = useMemo(() => {
    const d = new Date();
    const mo = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()];
    return `${d.getDate()} ${mo} '${String(d.getFullYear()).slice(2)}`;
  }, []);

  // Month boundary: show abbreviated month name when month changes (daily view)
  const periodMonthLabel = useMemo(() => {
    if (filters.groupBy !== 'day') return {} as Record<number, string>;
    const out: Record<number, string> = {};
    const MO = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    periods.forEach((p, pi) => {
      // Use month from data if available, else infer from periodLabel
      const mon = p.month != null ? MO[p.month - 1] : p.periodLabel.split(' ')[1];
      const prevMon = pi === 0 ? null :
        (periods[pi-1].month != null ? MO[periods[pi-1].month! - 1] : periods[pi-1].periodLabel.split(' ')[1]);
      if (pi === 0 || mon !== prevMon) out[pi] = mon ?? '';
    });
    return out;
  }, [periods, filters.groupBy]);

  // ── CSV export ─────────────────────────────────────────────────────────────
  const exportCSV = () => {
    const allTools = toolSet;
    const header   = ['Tool','Category',...periods.map(p=>p.periodLabel),'Total'].join(',');
    const rows     = allTools.map(tool => {
      const counts = periods.map(p => p.tools.find(t=>t.tool===tool)?.count ?? 0);
      return [tool, getCategory(tool).name, ...counts, toolTotals[tool]??0].join(',');
    });
    const csv  = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type:'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `tool-activity-${filters.dateFrom}-${filters.dateTo}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <motion.div className="th-card" layout transition={{ duration:0.3, ease:[0.23,1,0.32,1] }}>

        {/* ── Header ─────────────────────────────────────────────────── */}
        <button className="th-header" onClick={() => setOpen(o=>!o)} aria-expanded={open}>
          <span className="th-header__icon"><FiBarChart2 size={13} /></span>
          <span className="th-header__title">Agent Tool Activity</span>
          {!loading && <span className="th-header__month">{headerLabel}</span>}
          <span className="th-header__tag">{filters.groupBy==='month'?'Tool × Month':filters.groupBy==='day'?'Tool × Day':'Tool × Week'}</span>
          <span className="th-header__meta">{loading?'loading…':'live'}</span>
          <motion.span className="th-header__chevron"
            animate={{ rotate: open?0:-90 }} transition={{ duration:0.25 }}>
            <FiChevronDown size={13} />
          </motion.span>
        </button>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div className="th-body th-anim-clip" key="body"
              initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:'auto' }}
              exit={{ opacity:0, height:0 }}
              transition={{ duration:0.3, ease:[0.23,1,0.32,1] }}>

              {/* ── KPI strip ────────────────────────────────────────── */}
              <div className="th-kpi-strip">
                {([
                  { val: loading?'—':(summary?.grandTotal.toLocaleString()??'—'),      delta: wowDeltas.overall,  lbl:'Total Calls',  accent:false },
                  { val: loading?'—':(summary?.peakPeriodLabel??'—'),                  delta: null,               lbl:'Peak Period',   accent:true  },
                  { val: loading?'—':shortTool(summary?.topTool??'—',16),              delta: null,               lbl:'Top Tool',      accent:false },
                  { val: loading?'—':String(summary?.uniqueTotalTools??'—'),           delta: null,               lbl:'Tools',         accent:false },
                  { val: loading?'—':String(summary?.avgPerPeriod??'—'),               delta: null,               lbl:`Avg/${filters.groupBy==='month'?'Mo':filters.groupBy==='day'?'Day':'Wk'}`, accent:false },
                ] as const).map((k, i) => (
                  <motion.div key={i}
                    className={`th-kpi-chip${k.accent?' th-kpi-chip--accent':''}`}
                    initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }}
                    transition={{ delay:i*0.05, duration:0.2 }}>
                    <span className="th-kpi-chip__val" title={k.val}>{k.val}</span>
                    {'delta' in k && k.delta !== null && <Delta pct={k.delta} />}
                    <span className="th-kpi-chip__lbl">{k.lbl}</span>
                  </motion.div>
                ))}
              </div>

              {/* ── Filter toolbar ───────────────────────────────────── */}
              <div className="th-filter-bar">
                <div className="th-search-wrap">
                  <FiSearch size={11} className="th-search-icon" />
                  <input className="th-search-input" placeholder="Search tools…"
                    value={searchDraft} onChange={e => onSearchChange(e.target.value)} />
                  {searchDraft && (
                    <button className="th-search-clear" onClick={clearSearch}><FiX size={10} /></button>
                  )}
                </div>

                <select className="th-select" value={filters.groupBy}
                  onChange={e => applyFilter('groupBy', e.target.value as GroupBy)}>
                  <option value="day">Daily</option>
                  <option value="week">Weekly</option>
                  <option value="month">Monthly</option>
                </select>

                <select className="th-select" value={filters.topN}
                  onChange={e => applyFilter('topN', Number(e.target.value) as TopN)}>
                  {TOP_N_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>

                <div className="th-date-range">
                  <input type="date" className="th-date-input" value={filters.dateFrom}
                    onChange={e => applyFilter('dateFrom', e.target.value)} />
                  <span className="th-date-sep">→</span>
                  <input type="date" className="th-date-input" value={filters.dateTo}
                    onChange={e => applyFilter('dateTo', e.target.value)} />
                </div>

                <div className="th-filter-actions">
                  <button className="th-ctrl-btn"
                    onClick={() => setSortMode(m => SORT_CYCLE[(SORT_CYCLE.indexOf(m)+1)%SORT_CYCLE.length])}>
                    ↕ {SORT_LABEL[sortMode]}
                  </button>
                  <button className={`th-ctrl-btn${normalized?' th-ctrl-btn--on':''}`}
                    onClick={() => setNormalized(n=>!n)}>
                    {normalized?'% of Period':'Raw Count'}
                  </button>
                  <span className="th-filter-sep" />
                  <button className="th-ctrl-btn" onClick={exportCSV} title="Export CSV">↓ CSV</button>
                  {activeFilterCount > 0 && (
                    <button className="th-reset-btn th-reset-btn--icon" onClick={resetFilters} title="Reset filters">
                      <FiX size={11} />
                    </button>
                  )}
                  <button className={`th-ctrl-btn th-ctrl-btn--icon${activeFilterCount>0?' th-ctrl-btn--on':''}`}
                    title={activeFilterCount>0?`${activeFilterCount} active filter${activeFilterCount>1?'s':''}`:'Filters'}>
                    <FiFilter size={11} />
                    {activeFilterCount > 0 && <span className="th-ctrl-btn__badge">{activeFilterCount}</span>}
                  </button>
                </div>
              </div>

              {/* ── Error / empty ──────────────────────────────────────── */}
              {error && (
                <div className="th-empty">
                  <span className="th-empty__error-text">{error}</span>
                  <button className="th-ctrl-btn th-ctrl-btn--retry"
                    onClick={() => fetchData(filters)}>Retry</button>
                </div>
              )}
              {!error && !loading && periods.length===0 && (
                <div className="th-empty">No data for the selected filters.</div>
              )}

              {/* ── Heatmap ──────────────────────────────────────────── */}
              {!error && periods.length>0 && (
                <div className="th-heatmap" onMouseLeave={onLeave}
                  style={{ '--th-cols': periods.length } as React.CSSProperties}>

                  <div className="th-axis-labels">
                    <span className="th-axis-y">Tool ↓</span>
                    <span className="th-axis-x">{filters.groupBy==='month'?'Month →':filters.groupBy==='day'?'Day →':'Week →'}</span>
                  </div>

                  {/* Period header */}
                  <div className="th-hm-row th-hm-row--dow">
                    <div className="th-hm-label" />
                    {periods.map((p, pi) => {
                      const isToday = filters.groupBy === 'day' && p.periodLabel === todayLabel;
                      const monthLabel = periodMonthLabel[pi];
                      return (
                        <div key={pi}
                          className={['th-hm-daycol', hoverPeriod===pi?'th-hm-daycol--active':'', isToday?'th-hm-daycol--today':'']
                            .filter(Boolean).join(' ')}>
                          {monthLabel && (
                            <span className="th-hm-monthlabel">{monthLabel}</span>
                          )}
                          <span className="th-hm-daylabel" title={p.periodLabel}>
                            {filters.groupBy==='month'
                              ? p.periodLabel.slice(0, 3)
                              : filters.groupBy==='day'
                              ? String(p.period)
                              : `W${p.period}`}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Category groups */}
                  {groupedTools.map(({ category, tools }) => {
                    const catColor    = CATEGORY_COLORS[category];
                    const isCollapsed = collapsedGroups.has(category);
                    const catTotal    = tools.reduce((s,t) => s+(toolTotals[t]??0), 0);

                    return (
                      <React.Fragment key={category}>
                        {/* Category header row */}
                        <div className="th-cat-row" onClick={() => toggleGroup(category)}>
                          <div className="th-cat-label" style={{ borderLeftColor: catColor }}>
                            <motion.span className="th-cat-chevron"
                              animate={{ rotate: isCollapsed ? -90 : 0 }}
                              transition={{ duration: 0.18 }}>
                              <FiChevronRight size={9} />
                            </motion.span>
                            <span className="th-cat-name" style={{ color: catColor }}>{category}</span>
                            <span className="th-cat-meta">{tools.length} tools · {catTotal.toLocaleString()}</span>
                          </div>
                          {/* Per-period totals for this category */}
                          {periods.map((p, pi) => {
                            const total = tools.reduce((s,t) => s+(p.tools.find(tt=>tt.tool===t)?.count??0), 0);
                            const h = maxPeriodTotal > 0 ? Math.max((total/maxPeriodTotal)*8,0) : 0;
                            return (
                              <div key={pi} className="th-cat-cell"
                                style={{ borderBottom: h>0 ? `2px solid ${catColor}40` : '2px solid transparent' }} />
                            );
                          })}
                        </div>

                        {/* Tool rows */}
                        <AnimatePresence initial={false}>
                          {!isCollapsed && tools.map((tool, ti) => {
                            const catCol   = getCategory(tool).color;
                            const sparkVals = periods.map(p => p.tools.find(t=>t.tool===tool)?.count??0);
                            return (
                              <motion.div key={tool}
                                className={`th-hm-row${hoverTool===tool?' th-hm-row--active':''}`}
                                initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:'auto' }}
                                exit={{ opacity:0, height:0 }}
                                transition={{ delay:ti*0.02, duration:0.18 }}>

                                <div className={`th-hm-label${hoverTool===tool?' th-hm-label--active':''}`}>
                                  <span className={`th-trend th-trend--${toolTrends[tool]}`}>
                                    {toolTrends[tool]==='up'?'↑':toolTrends[tool]==='down'?'↓':'→'}
                                  </span>
                                  <span className="th-hm-label__name" title={tool}>{shortTool(tool)}</span>
                                  <span className="th-hm-label__spark">
                                    <Sparkline values={sparkVals} color={catCol} />
                                  </span>
                                  <AnimatePresence>
                                    {hoverTool===tool && (
                                      <motion.span className="th-hm-label__total"
                                        initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }}
                                        exit={{ opacity:0 }} transition={{ duration:0.1 }}>
                                        {toolTotals[tool]}
                                        {wowDeltas.tools[tool] !== null && wowDeltas.tools[tool] !== undefined && (
                                          <Delta pct={wowDeltas.tools[tool]!} />
                                        )}
                                      </motion.span>
                                    )}
                                  </AnimatePresence>
                                </div>

                                {periods.map((period, pi) => {
                                  const count  = period.tools.find(t=>t.tool===tool)?.count??0;
                                  const level  = normalized ? getNormLevel(count,period.totalCount) : getLevel(count,maxVal);
                                  const isPeak = tool===peakCell.tool && pi===peakCell.pi && !normalized;
                                  const isSel  = selectedCell?.tool===tool && selectedCell?.pi===pi;
                                  const inRow  = hoverTool===tool;
                                  const inCol  = hoverPeriod===pi;
                                  const isHov  = inRow && inCol;
                                  const cross  = !isHov && (inRow||inCol);
                                  const dimmed = (hoverTool!==null||hoverPeriod!==null) && !inRow && !inCol;
                                  return (
                                    <motion.div key={pi}
                                      className={['th-hm-cell',
                                        count>0?'th-hm-cell--active':'',
                                        isHov?'th-hm-cell--hovered':'',
                                        cross?'th-hm-cell--crosshair':'',
                                        dimmed?'th-hm-cell--dimmed':'',
                                        isPeak?'th-hm-cell--peak':'',
                                        isSel?'th-hm-cell--selected':'',
                                      ].filter(Boolean).join(' ')}
                                      style={{ background:getLevelBg(normalized,level), boxShadow:LEVEL_SHADOW[level] }}
                                      onMouseEnter={e=>onEnter(e,tool,pi)} onMouseMove={onMove}
                                      onClick={() => onCellClick(tool,pi)}
                                      initial={{ opacity:0, scale:0.45 }} animate={{ opacity:1, scale:1 }}
                                      transition={{ delay:0.05+(ti*periods.length+pi)*0.003, duration:0.12 }}
                                    />
                                  );
                                })}
                              </motion.div>
                            );
                          })}
                        </AnimatePresence>
                      </React.Fragment>
                    );
                  })}

                  {/* Micro-bars */}
                  <div className="th-hm-row th-hm-row--microbars">
                    <div className="th-hm-label th-hm-label--micro">Total</div>
                    {periods.map((p, pi) => (
                      <div key={pi}
                        className={['th-hm-microbar-wrap', hoverPeriod===pi?'th-hm-microbar-wrap--active':'']
                          .filter(Boolean).join(' ')}>
                        <motion.div className="th-hm-microbar"
                          style={{ height:`${Math.max((p.totalCount/maxPeriodTotal)*12,2)}px` }}
                          initial={{ scaleY:0 }} animate={{ scaleY:1 }}
                          transition={{ delay:0.55+pi*0.04, duration:0.24, ease:'easeOut' }} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Drill-down ───────────────────────────────────────── */}
              <AnimatePresence>
                {drill && (
                  <motion.div className="th-drill th-anim-clip"
                    initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:'auto' }}
                    exit={{ opacity:0, height:0 }}
                    transition={{ duration:0.22, ease:[0.23,1,0.32,1] }}>
                    <div className="th-drill__inner">
                      <div className="th-drill__left">
                        <div className="th-drill__cat-row">
                          <span className="th-drill__cat-dot" style={{ background: drill.catColor }} />
                          <span className="th-drill__tool" title={drill.tool}>{shortTool(drill.tool,28)}</span>
                        </div>
                        <span className="th-drill__date">
                          {drill.period.periodLabel}
                          <span className="th-drill__cat-badge" style={{ color:drill.catColor, borderColor:`${drill.catColor}40`, background:`${drill.catColor}12` }}>
                            {getCategory(drill.tool).name}
                          </span>
                          {isAnomaly(drill.tool, drill.count) && <span className="th-drill__anomaly-badge">⚡ Spike</span>}
                        </span>
                      </div>
                      <div className="th-drill__stats">
                        <div className="th-drill__stat">
                          <span className="th-drill__stat-val">{drill.count}</span>
                          <span className="th-drill__stat-lbl">uses</span>
                        </div>
                        <div className="th-drill__stat">
                          <span className={`th-drill__stat-val ${drill.diffPct>=0?'th-drill__pos':'th-drill__neg'}`}>
                            {drill.diffPct>=0?'+':''}{drill.diffPct}%
                          </span>
                          <span className="th-drill__stat-lbl">vs avg ({drill.avg})</span>
                        </div>
                        <div className="th-drill__stat">
                          <span className="th-drill__stat-val">{drill.pctOf}%</span>
                          <span className="th-drill__stat-lbl">of period</span>
                        </div>
                        <div className="th-drill__stat">
                          <div className="th-drill__dots">
                            {[1,2,3,4,5].map(lv => (
                              <span key={lv}
                                className={`th-drill__dot${drill.level>=lv?' th-drill__dot--on':''}`}
                                style={drill.level>=lv?{background:getLevelBg(false,lv)}:undefined} />
                            ))}
                          </div>
                          <span className="th-drill__stat-lbl">intensity</span>
                        </div>
                      </div>
                      <button className="th-drill__close" onClick={() => setSelectedCell(null)}>×</button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* ── Footer ───────────────────────────────────────────── */}
              <div className="th-footer">
                <div className="th-legend">
                  <span className="th-legend__lbl">Less</span>
                  <div className="th-legend__swatches">
                    {[0,1,2,3,4,5].map(lv => (
                      <div key={lv} className="th-legend__swatch" style={{ background:getLevelBg(normalized,lv) }} />
                    ))}
                  </div>
                  <span className="th-legend__lbl">More</span>
                </div>
                <span className="th-footer__divider" />
                {/* Category legend */}
                <div className="th-cat-legend">
                  {CATEGORY_ORDER.filter(c => groupedTools.some(g=>g.category===c)).map(c => (
                    <span key={c} className="th-cat-legend-item">
                      <span className="th-cat-legend-dot" style={{ background: CATEGORY_COLORS[c] }} />
                      {c}
                    </span>
                  ))}
                </div>
                <span className="th-footer__divider" />
                <div className="th-trend-legend">
                  <span className="th-trend th-trend--up">↑</span>
                  <span className="th-trend th-trend--flat">→</span>
                  <span className="th-trend th-trend--down">↓</span>
                  <span>trend</span>
                </div>
                {summary && (
                  <>
                    <span className="th-footer__divider" />
                    <span className="th-footer__note th-footer__note--dim">
                      {summary.periodsReturned} {filters.groupBy==='month'?'months':filters.groupBy==='day'?'days':'weeks'} · {summary.uniqueTotalTools} tools
                    </span>
                  </>
                )}
              </div>

            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* ── Tooltip ──────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {tooltip && (
          <motion.div className="th-tooltip"
            style={{ left:tooltip.x+14, top:tooltip.y-92 }}
            initial={{ opacity:0, y:4, scale:0.93 }} animate={{ opacity:1, y:0, scale:1 }}
            exit={{ opacity:0, scale:0.93 }} transition={{ duration:0.1 }}>
            <span className="th-tooltip__tool" title={tooltip.tool}>{shortTool(tooltip.tool,22)}</span>
            <span className="th-tooltip__date">{periods[tooltip.pi]?.periodLabel??''}</span>
            <div className="th-tooltip__stats">
              <span className="th-tooltip__count">{tooltip.count>0?`${tooltip.count} uses`:'No activity'}</span>
              {tooltip.count>0 && periods[tooltip.pi]?.totalCount>0 && (
                <span className="th-tooltip__pct">
                  {Math.round((tooltip.count/periods[tooltip.pi].totalCount)*100)}% of period
                </span>
              )}
            </div>
            {tooltip.count>0 && <span className="th-tooltip__hint">click to drill down</span>}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default TransitionHeatmap;

